import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Download, LogOut, MailCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { API } from "../data/site";
import { SEO } from "@/components/Seo";

const CITY_LABEL = { delhi_ncr: "Delhi NCR", bengaluru: "Bengaluru", other: "Other" };

export default function AdminDashboard() {
  const [leads, setLeads] = useState(null);
  const [city, setCity] = useState("all");
  const [sending, setSending] = useState(false);
  const [sendingTopic, setSendingTopic] = useState(null);
  const [teasers, setTeasers] = useState({});
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [giftLeads, setGiftLeads] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [catDraft, setCatDraft] = useState(null);
  const [catEditing, setCatEditing] = useState(false);
  const [catAudience, setCatAudience] = useState("all");
  const [catSending, setCatSending] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("pk_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    axios
      .get(`${API}/leads`, { headers })
      .then((r) => setLeads(r.data.leads))
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem("pk_admin_token");
          navigate("/admin");
        }
      });
    axios
      .get(`${API}/admin/giftbox-leads`, { headers })
      .then((r) => setGiftLeads(r.data.leads))
      .catch(() => {});
  };

  useEffect(() => {
    if (!token) return navigate("/admin");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/newsletter-config`, { headers })
      .then((r) => setTeasers(Object.fromEntries(r.data.topics.map((t) => [t.slug, t.teaser]))))
      .catch(() => {});
    axios
      .get(`${API}/admin/gift-catalogue`, { headers })
      .then((r) => setCatalogue(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () => (leads || []).filter((l) => city === "all" || l.city === city),
    [leads, city]
  );

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "WhatsApp", "City", "Interest", "Source", "Signed Up", "Drop Reminder"],
      ...filtered.map((l) => [
        l.name || "", l.email, l.whatsapp || "", CITY_LABEL[l.city] || l.city,
        (l.topics && l.topics.length ? l.topics.join(", ") : l.interest || ""), l.source || "", l.created_at, l.drop_reminder_sent ? "sent" : "pending",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "pranivaa-waitlist.csv";
    a.click();
  };

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/drop-reminders`, {}, { headers });
      toast.success(`Drop reminder sent to ${data.sent} ${data.sent === 1 ? "member" : "members"}.`);
      load();
    } catch {
      toast.error("Could not send reminders right now.");
    } finally {
      setSending(false);
    }
  };

  const counts = useMemo(() => ({
    total: (leads || []).length,
    delhi: (leads || []).filter((l) => l.city === "delhi_ncr").length,
    blr: (leads || []).filter((l) => l.city === "bengaluru").length,
  }), [leads]);

  return (
    <div data-testid="admin-dashboard" className="pt-24 md:pt-32 pb-20">
      <SEO title="Kitchen Office Dashboard | Pranivaa Kitchens" description="Admin dashboard." path="/admin/dashboard" noindex />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-2">Kitchen Office</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-ink">Waitlist Leads</h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="send-reminders-button"
              onClick={sendReminders}
              disabled={sending}
              className="inline-flex items-center gap-2 rounded-full bg-turmeric text-ink text-xs font-bold px-5 py-3 transition-all hover:bg-turmeric-deep disabled:opacity-60"
            >
              <MailCheck size={14} /> {sending ? "Sending..." : "Send Drop Reminders"}
            </button>
            <button
              data-testid="send-digest-button"
              onClick={async () => {
                try {
                  const { data } = await axios.post(`${API}/admin/send-digest`, {}, { headers });
                  toast.success(`Weekly digest sent — ${data.new_signups} new signups this week.`);
                } catch {
                  toast.error("Could not send the digest right now.");
                }
              }}
              className="inline-flex items-center gap-2 rounded-full border border-[#D6CEBF] text-ink-soft text-xs font-bold px-5 py-3 transition-all hover:border-ink"
            >
              <MailCheck size={14} /> Send Weekly Digest
            </button>
            <button
              data-testid="export-csv-button"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-full bg-leaf text-white text-xs font-bold px-5 py-3 transition-all hover:bg-leaf-deep"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={() => { localStorage.removeItem("pk_admin_token"); navigate("/admin"); }}
              className="inline-flex items-center gap-2 rounded-full border border-[#D6CEBF] text-ink-soft text-xs font-bold px-5 py-3 transition-all hover:border-ink"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[["Total signups", counts.total, "leads-stat-total"], ["Delhi NCR", counts.delhi, "leads-stat-delhi"], ["Bengaluru", counts.blr, "leads-stat-bengaluru"]].map(([label, val, tid]) => (
            <div key={tid} data-testid={tid} className="bg-paper-card border border-[#E7E2D7] rounded-2xl p-6 flex items-center gap-4">
              <span className="w-10 h-10 rounded-full bg-turmeric-light text-turmeric-deep flex items-center justify-center"><Users size={17} /></span>
              <div>
                <p className="font-mono text-2xl font-semibold text-ink">{val}</p>
                <p className="text-xs text-ink-muted">{label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-6" data-testid="city-filter-group">
          {[["all", "All cities"], ["delhi_ncr", "Delhi NCR"], ["bengaluru", "Bengaluru"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`city-filter-${id}`}
              onClick={() => setCity(id)}
              className={`rounded-full px-4 py-2 text-xs font-semibold border transition-all ${
                city === id ? "bg-beet text-white border-beet" : "border-[#D6CEBF] text-ink-soft hover:border-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="bg-paper-card border border-[#E7E2D7] rounded-2xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">            <thead>
              <tr className="text-left font-mono text-[11px] uppercase tracking-[0.12em] text-ink-muted border-b border-[#E7E2D7]">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">WhatsApp</th>
                <th className="px-5 py-4">City</th>
                <th className="px-5 py-4">Interest</th>
                <th className="px-5 py-4">Signed Up</th>
                <th className="px-5 py-4">Reminder</th>
              </tr>
            </thead>
            <tbody>
              {(filtered || []).map((l) => (
                <tr key={l.id || l.email} className="border-b border-[#F0ECE1] last:border-0 hover:bg-paper-muted/40">
                  <td className="px-5 py-3.5 font-medium text-ink">{l.name || "—"}</td>
                  <td className="px-5 py-3.5 text-ink-soft">{l.email}</td>
                  <td className="px-5 py-3.5 text-ink-soft">{l.whatsapp || "—"}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${l.city === "delhi_ncr" ? "bg-turmeric-light text-turmeric-deep" : l.city === "bengaluru" ? "bg-leaf-light text-leaf" : "bg-paper-muted text-ink-muted"}`}>
                      {CITY_LABEL[l.city] || l.city}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-ink-soft text-xs">{l.topics && l.topics.length ? l.topics.join(", ") : l.interest || "—"}</td>
                  <td className="px-5 py-3.5 text-ink-muted text-xs font-mono">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-[11px] font-mono font-semibold ${l.drop_reminder_sent ? "text-leaf" : "text-ink-muted"}`}>
                      {l.drop_reminder_sent ? "sent" : "pending"}
                    </span>
                  </td>
                </tr>
              ))}
              {leads && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-ink-muted text-sm">No signups yet for this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-10 bg-paper-card border border-[#E7E2D7] rounded-2xl p-7" data-testid="topic-newsletter-section">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-2">Topic Newsletters</p>
          <h2 className="font-serif text-2xl font-semibold text-ink mb-2">Send a guide update</h2>
          <p className="text-sm text-ink-soft mb-6">
            Goes only to members who picked that topic on the waitlist form. Fixed template, one tap.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              ["diabetes", "Diabetes"], ["pcos", "PCOS"], ["thyroid", "Thyroid"],
              ["gut-health", "Gut Health"], ["heart-health", "Heart Health"], ["weight-management", "Weight"],
            ].map(([slug, label]) => {
              const n = (leads || []).filter((l) =>
                (l.topics && l.topics.length ? l.topics : l.interest ? [l.interest] : []).includes(slug)
              ).length;
              return (
                <div
                  key={slug}
                  data-testid={`newsletter-topic-${slug}`}
                  className="border border-[#E7E2D7] rounded-xl p-4 flex flex-col transition-all duration-300 hover:border-clay/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-sm text-ink">{label}</p>
                    <span className="text-[11px] font-mono text-ink-muted shrink-0">
                      {n} {n === 1 ? "member" : "members"}
                    </span>
                  </div>
                  {editing === slug ? (
                    <div className="mt-3 space-y-2">
                      <textarea
                        data-testid={`newsletter-teaser-input-${slug}`}
                        className="w-full bg-paper border border-[#D6CEBF] rounded-lg px-3 py-2 text-xs text-ink leading-relaxed min-h-24 focus:outline-none focus:ring-2 focus:ring-turmeric/70"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        maxLength={400}
                      />
                      <div className="flex gap-2">
                        <button
                          data-testid={`newsletter-teaser-save-${slug}`}
                          onClick={async () => {
                            try {
                              const { data } = await axios.put(
                                `${API}/admin/newsletter-config`,
                                { topic: slug, teaser: draft },
                                { headers }
                              );
                              setTeasers({ ...teasers, [slug]: data.teaser });
                              setEditing(null);
                              toast.success("Teaser updated.");
                            } catch {
                              toast.error("Could not save. Keep it between 10 and 400 characters.");
                            }
                          }}
                          className="rounded-full bg-leaf text-white text-[11px] font-bold px-4 py-2 transition-all hover:bg-leaf-deep"
                        >
                          Save
                        </button>
                        <button
                          data-testid={`newsletter-teaser-cancel-${slug}`}
                          onClick={() => setEditing(null)}
                          className="rounded-full border border-[#D6CEBF] text-ink-soft text-[11px] font-bold px-4 py-2 transition-all hover:border-ink"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-ink-muted mt-2 leading-relaxed flex-1">{teasers[slug] || "…"}</p>
                      <div className="flex gap-2 mt-3">
                        <button
                          data-testid={`newsletter-send-${slug}`}
                          disabled={n === 0 || sendingTopic === slug}
                          onClick={async () => {
                            setSendingTopic(slug);
                            try {
                              const { data } = await axios.post(`${API}/admin/topic-newsletter`, { topic: slug }, { headers });
                              toast.success(`"${label}" update sent to ${data.sent} ${data.sent === 1 ? "member" : "members"}.`);
                            } catch {
                              toast.error("Could not send right now.");
                            } finally {
                              setSendingTopic(null);
                            }
                          }}
                          className="rounded-full bg-beet text-white text-[11px] font-bold px-4 py-2 transition-all hover:bg-beet-deep disabled:opacity-50"
                        >
                          {sendingTopic === slug ? "Sending..." : n === 0 ? "No members yet" : "Send update"}
                        </button>
                        <button
                          data-testid={`newsletter-edit-${slug}`}
                          onClick={() => { setEditing(slug); setDraft(teasers[slug] || ""); }}
                          className="rounded-full border border-[#D6CEBF] text-ink-soft text-[11px] font-bold px-4 py-2 transition-all hover:border-ink"
                        >
                          Edit teaser
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-10 bg-paper-card border border-[#E7E2D7] rounded-2xl p-7" data-testid="gift-catalogue-section">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-2">Diwali Gifting</p>
              <h2 className="font-serif text-2xl font-semibold text-ink">Gift Catalogue Email</h2>
              <p className="text-sm text-ink-soft mt-1">Edit items and pricing, then send to your gift box leads.</p>
            </div>
            {!catEditing && catalogue && (
              <div className="flex flex-wrap items-center gap-2">
                {[
                  ["all", giftLeads.length],
                  ["corporate", giftLeads.filter((g) => g.gifting === "corporate").length],
                  ["family", giftLeads.filter((g) => g.gifting === "family").length],
                ].map(([id, n]) => (
                  <button
                    key={id}
                    data-testid={`catalogue-audience-${id}`}
                    onClick={() => setCatAudience(id)}
                    className={`rounded-full px-3.5 py-2 text-[11px] font-bold border transition-all ${
                      catAudience === id ? "bg-beet text-white border-beet" : "border-[#D6CEBF] text-ink-soft hover:border-ink"
                    }`}
                  >
                    {id === "all" ? "All" : id === "corporate" ? "Corporate" : "Family"} ({n})
                  </button>
                ))}
                <button
                  data-testid="catalogue-send-button"
                  disabled={catSending || giftLeads.length === 0}
                  onClick={async () => {
                    setCatSending(true);
                    try {
                      const { data } = await axios.post(`${API}/admin/send-gift-catalogue`, { audience: catAudience }, { headers });
                      toast.success(`Catalogue sent to ${data.sent} ${data.sent === 1 ? "lead" : "leads"}.`);
                    } catch {
                      toast.error("Could not send right now.");
                    } finally {
                      setCatSending(false);
                    }
                  }}
                  className="rounded-full bg-leaf text-white text-[11px] font-bold px-5 py-2.5 transition-all hover:bg-leaf-deep disabled:opacity-50"
                >
                  {catSending ? "Sending..." : "Send Catalogue"}
                </button>
                <button
                  data-testid="catalogue-edit-button"
                  onClick={() => { setCatDraft(JSON.parse(JSON.stringify(catalogue))); setCatEditing(true); }}
                  className="rounded-full border border-[#D6CEBF] text-ink-soft text-[11px] font-bold px-5 py-2.5 transition-all hover:border-ink"
                >
                  Edit Catalogue
                </button>
              </div>
            )}
          </div>
          {catEditing && catDraft ? (
            <div className="space-y-3" data-testid="catalogue-editor">
              <textarea
                data-testid="catalogue-intro-input"
                className="w-full bg-paper border border-[#D6CEBF] rounded-lg px-3 py-2 text-xs text-ink leading-relaxed min-h-20 focus:outline-none focus:ring-2 focus:ring-turmeric/70"
                value={catDraft.intro}
                maxLength={600}
                onChange={(e) => setCatDraft({ ...catDraft, intro: e.target.value })}
              />
              {catDraft.items.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    data-testid={`catalogue-item-name-${i}`}
                    className="col-span-12 sm:col-span-5 bg-paper border border-[#D6CEBF] rounded-lg px-3 py-2 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-turmeric/70"
                    placeholder="Item name"
                    value={item.name}
                    onChange={(e) => {
                      const items = [...catDraft.items];
                      items[i] = { ...items[i], name: e.target.value };
                      setCatDraft({ ...catDraft, items });
                    }}
                  />
                  <input
                    data-testid={`catalogue-item-desc-${i}`}
                    className="col-span-8 sm:col-span-4 bg-paper border border-[#D6CEBF] rounded-lg px-3 py-2 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-turmeric/70"
                    placeholder="Description"
                    value={item.desc}
                    onChange={(e) => {
                      const items = [...catDraft.items];
                      items[i] = { ...items[i], desc: e.target.value };
                      setCatDraft({ ...catDraft, items });
                    }}
                  />
                  <input
                    data-testid={`catalogue-item-price-${i}`}
                    className="col-span-3 sm:col-span-2 bg-paper border border-[#D6CEBF] rounded-lg px-3 py-2 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-turmeric/70"
                    placeholder="Price"
                    value={item.price}
                    onChange={(e) => {
                      const items = [...catDraft.items];
                      items[i] = { ...items[i], price: e.target.value };
                      setCatDraft({ ...catDraft, items });
                    }}
                  />
                  <button
                    data-testid={`catalogue-item-remove-${i}`}
                    onClick={() => setCatDraft({ ...catDraft, items: catDraft.items.filter((_, j) => j !== i) })}
                    className="col-span-1 text-ink-muted hover:text-beet text-lg leading-none"
                    aria-label="Remove item"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                data-testid="catalogue-add-item"
                onClick={() => setCatDraft({ ...catDraft, items: [...catDraft.items, { name: "", desc: "", price: "" }] })}
                disabled={catDraft.items.length >= 10}
                className="rounded-full border border-dashed border-[#D6CEBF] text-ink-soft text-[11px] font-bold px-4 py-2 transition-all hover:border-ink disabled:opacity-50"
              >
                + Add item
              </button>
              <input
                data-testid="catalogue-note-input"
                className="w-full bg-paper border border-[#D6CEBF] rounded-lg px-3 py-2 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-turmeric/70"
                placeholder="Footer note (e.g. corporate bulk terms)"
                value={catDraft.note}
                maxLength={300}
                onChange={(e) => setCatDraft({ ...catDraft, note: e.target.value })}
              />
              <div className="flex gap-2 pt-1">
                <button
                  data-testid="catalogue-save-button"
                  onClick={async () => {
                    try {
                      const { data } = await axios.put(`${API}/admin/gift-catalogue`, catDraft, { headers });
                      setCatalogue(data);
                      setCatEditing(false);
                      toast.success("Catalogue saved.");
                    } catch (err) {
                      const d = err?.response?.data?.detail;
                      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Could not save. Check intro length and item names.");
                    }
                  }}
                  className="rounded-full bg-leaf text-white text-[11px] font-bold px-5 py-2.5 transition-all hover:bg-leaf-deep"
                >
                  Save Catalogue
                </button>
                <button
                  data-testid="catalogue-cancel-button"
                  onClick={() => setCatEditing(false)}
                  className="rounded-full border border-[#D6CEBF] text-ink-soft text-[11px] font-bold px-5 py-2.5 transition-all hover:border-ink"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : catalogue ? (
            <div>
              <p className="text-xs text-ink-muted leading-relaxed mb-4 max-w-2xl">{catalogue.intro}</p>
              <ul className="divide-y divide-[#F0ECE1]">
                {catalogue.items.map((it, i) => (
                  <li key={i} className="py-2.5 flex justify-between gap-4" data-testid={`catalogue-preview-item-${i}`}>
                    <div>
                      <p className="text-sm font-medium text-ink">{it.name}</p>
                      <p className="text-xs text-ink-muted">{it.desc}</p>
                    </div>
                    <span className="text-sm font-semibold text-beet whitespace-nowrap">{it.price}</span>
                  </li>
                ))}
              </ul>
              {catalogue.note && <p className="text-xs text-ink-muted mt-3 italic">{catalogue.note}</p>}
            </div>
          ) : (
            <p className="text-sm text-ink-muted">Loading catalogue…</p>
          )}
        </div>

        <div className="mt-10 bg-paper-card border border-[#E7E2D7] rounded-2xl p-7" data-testid="giftbox-leads-section">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
            <div>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-2">Diwali Gifting</p>
              <h2 className="font-serif text-2xl font-semibold text-ink">Gift Box Leads</h2>
            </div>
            <p className="text-xs font-mono text-ink-muted" data-testid="giftbox-leads-count">
              {giftLeads.filter((g) => g.gifting === "corporate").length} corporate · {giftLeads.filter((g) => g.gifting === "family").length} family
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="giftbox-leads-table">
              <thead>
                <tr className="text-left font-mono text-[11px] uppercase tracking-[0.12em] text-ink-muted border-b border-[#E7E2D7]">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">WhatsApp</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Boxes</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {giftLeads.map((g) => (
                  <tr key={g.id || g.email} className="border-b border-[#F0ECE1] last:border-0 hover:bg-paper-muted/40">
                    <td className="px-4 py-3 font-medium text-ink">{g.name || "—"}</td>
                    <td className="px-4 py-3 text-ink-soft">{g.email}</td>
                    <td className="px-4 py-3 text-ink-soft">{g.whatsapp || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-semibold ${g.gifting === "corporate" ? "bg-clay-light text-clay-deep" : "bg-turmeric-light text-turmeric-deep"}`}>
                        {g.gifting === "corporate" ? "Corporate" : "Family"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-soft text-xs">{g.boxes || "—"}</td>
                    <td className="px-4 py-3 text-ink-soft text-xs">{CITY_LABEL[g.city] || g.city}</td>
                    <td className="px-4 py-3 text-ink-muted text-xs font-mono">{new Date(g.created_at).toLocaleDateString("en-IN")}</td>
                  </tr>
                ))}
                {giftLeads.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-ink-muted text-sm">No gift box interest yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
